/**
 * WHO MARKED EACH COSTUME STEP, AND EXACTLY WHEN (Bobbie, 2026-11-02: "it
 * should track who does what and who marks off what each date. it should
 * clear ly show who picked up the costume who checked it in and exactly
 * when").
 *
 * The server keeps the trail (netlify/functions/costumeStatus.mjs,
 * costumeStatusLog/{costumeId}); the Tracker card, the Costume report and its
 * Excel read it through here so every screen says it the same way:
 * "Ordered by Kim, Tue Nov 3, 4:12pm". Every time is STUDIO TIME,
 * America/Chicago, whatever clock the phone keeps.
 *
 * PURE. No React, no Firebase.
 */

/** 'handedOut': the whole-class Picked up on a card not handed out per dancer. */
export type CostumeStep = 'ordered' | 'arrived' | 'ready' | 'paid' | 'handedOut';
export const COSTUME_STEPS: readonly CostumeStep[] = ['ordered', 'arrived', 'ready', 'paid', 'handedOut'];

/**
 * Kim's cost and payment status for the card (2026-11-03), on the same
 * trail. Their stamp carries what it was set to (value): "$65.00", a payment
 * status id, or null for cleared.
 */
export type CostumeDetailStep = 'cost' | 'payment';
export type CostumeTrailStep = CostumeStep | CostumeDetailStep;
const TRAIL_STEPS: readonly CostumeTrailStep[] = [...COSTUME_STEPS, 'cost', 'payment'];

export interface CostumeTrailStamp {
  atMs: number | null;
  byName: string | null;
  /** cost and payment only: what it was set to; null when cleared. */
  value?: string | null;
}

export interface CostumeTrailEntry extends CostumeTrailStamp {
  step: CostumeTrailStep;
  undo: boolean;
}

export interface CostumeTrail {
  /** The press that stands for each step now; absent after an Undo. */
  current: Partial<Record<CostumeTrailStep, CostumeTrailStamp>>;
  /** Every press, oldest first (the server sends the last 30). */
  entries: CostumeTrailEntry[];
  /** The cost Kim entered for one dancer's costume, in cents; null when nobody did. */
  costCents?: number | null;
  /** Each Tell families now on Ready for payment: who, when, how many (2026-11-03). */
  paymentNotices?: CostumePaymentNotice[];
  /** The dancers whose grown-ups were told the card is ready for payment. */
  paymentTold?: string[];
}

export interface CostumePaymentNotice {
  atMs: number | null;
  byName: string | null;
  dancers: number;
  grownUps: number;
}

export const STUDIO_TIME_ZONE = 'America/Chicago';

export const COSTUME_STEP_DONE_LABELS: Record<CostumeStep, string> = {
  ordered: 'Ordered',
  arrived: 'Arrived',
  ready: 'Ready for pickup',
  paid: 'Paid',
  handedOut: 'Picked up',
};

const UNDO_LABELS: Record<CostumeStep, string> = {
  ordered: 'Ordered taken back',
  arrived: 'Arrived taken back',
  ready: 'Taken off ready for pickup',
  paid: 'Paid taken back',
  handedOut: 'Picked up taken back',
};

/** "Tue Nov 3, 4:12pm" in studio time, or '' for no time. */
export function studioStamp(atMs: number | null | undefined): string {
  if (typeof atMs !== 'number' || !Number.isFinite(atMs)) return '';
  const date = new Date(atMs);
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: STUDIO_TIME_ZONE, weekday: 'short', month: 'short', day: 'numeric',
  }).formatToParts(date);
  const part = (type: string) => day.find((p) => p.type === type)?.value ?? '';
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: STUDIO_TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(date).replace(/\s+/g, '').toLowerCase();
  return `${part('weekday')} ${part('month')} ${part('day')}, ${time}`;
}

/** "Kim, Tue Nov 3, 4:12pm"; a nameless account is never given a made-up name. */
export function stampWho(stamp: CostumeTrailStamp | null | undefined): string {
  if (!stamp) return '';
  const when = studioStamp(stamp.atMs);
  const who = stamp.byName?.trim() || '';
  return [who, when].filter(Boolean).join(', ');
}

/** "Ordered by Kim, Tue Nov 3, 4:12pm" for the step as it stands now, or null. */
export function currentStepLine(trail: CostumeTrail | null | undefined, step: CostumeStep): string | null {
  const stamp = trail?.current?.[step];
  if (!stamp) return null;
  const who = stamp.byName?.trim();
  const when = studioStamp(stamp.atMs);
  return `${COSTUME_STEP_DONE_LABELS[step]}${who ? ` by ${who}` : ''}${when ? `, ${when}` : ''}`;
}

const PAYMENT_WORDS: Record<string, string> = {
  'not-needed': 'No payment needed',
  needed: 'Payment needed',
  'ready-for-payment': 'Ready for payment',
  paid: 'Paid',
};

/** "Cost set to $65.00", "Payment status set to Ready for payment", or the clear. */
export function detailLabel(step: CostumeDetailStep, value: string | null | undefined): string {
  if (step === 'cost') return value ? `Cost set to ${value}` : 'Cost cleared';
  return value ? `Payment status set to ${PAYMENT_WORDS[value] ?? value}` : 'Payment status cleared';
}

/** One line per press, newest first. */
export function trailLines(trail: CostumeTrail | null | undefined): string[] {
  const entries = trail?.entries ?? [];
  return [...entries].reverse().map((entry) => {
    const label = entry.step === 'cost' || entry.step === 'payment'
      ? detailLabel(entry.step, entry.value)
      : entry.undo ? UNDO_LABELS[entry.step] : COSTUME_STEP_DONE_LABELS[entry.step];
    const who = entry.byName?.trim();
    const when = studioStamp(entry.atMs);
    return `${label}${who ? ` by ${who}` : ''}${when ? `, ${when}` : ''}`;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stampFrom(raw: unknown, withValue = false): CostumeTrailStamp | null {
  if (!isRecord(raw)) return null;
  return {
    atMs: typeof raw.atMs === 'number' && Number.isFinite(raw.atMs) ? raw.atMs : null,
    byName: typeof raw.byName === 'string' && raw.byName.trim() ? raw.byName.trim() : null,
    ...(withValue ? { value: typeof raw.value === 'string' && raw.value ? raw.value : null } : {}),
  };
}

const isDetail = (step: CostumeTrailStep) => step === 'cost' || step === 'payment';

/** A trail as the server sent it, checked field by field. */
export function normalizeCostumeTrail(raw: unknown): CostumeTrail {
  const out: CostumeTrail = { current: {}, entries: [] };
  if (!isRecord(raw)) return out;
  if (isRecord(raw.current)) {
    for (const step of TRAIL_STEPS) {
      const stamp = stampFrom(raw.current[step], isDetail(step));
      if (stamp) out.current[step] = stamp;
    }
  }
  if (Array.isArray(raw.entries)) {
    for (const row of raw.entries) {
      if (!isRecord(row) || !TRAIL_STEPS.includes(row.step as CostumeTrailStep)) continue;
      const step = row.step as CostumeTrailStep;
      const stamp = stampFrom(row, isDetail(step)) as CostumeTrailStamp;
      out.entries.push({ step, undo: row.undo === true, ...stamp });
    }
  }
  const cents = raw.costCents;
  out.costCents = typeof cents === 'number' && Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
  const count = (n: unknown) => (typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? n : 0);
  out.paymentNotices = (Array.isArray(raw.paymentNotices) ? raw.paymentNotices : [])
    .filter(isRecord)
    .map((n) => ({
      atMs: typeof n.atMs === 'number' && Number.isFinite(n.atMs) ? n.atMs : null,
      byName: typeof n.byName === 'string' && n.byName ? n.byName : null,
      dancers: count(n.dancers),
      grownUps: count(n.grownUps),
    }));
  out.paymentTold = (Array.isArray(raw.paymentTold) ? raw.paymentTold : []).filter((sid): sid is string => typeof sid === 'string' && !!sid);
  return out;
}

/** "$65.00", or '' for no cost. */
export function costText(cents: number | null | undefined): string {
  return typeof cents === 'number' && Number.isFinite(cents) ? `$${(cents / 100).toFixed(2)}` : '';
}
